import { useState, useRef, useEffect } from 'react';
import api from '../../api/axios';
import SuperadminLayout from '../../components/SuperadminLayout';
import useAuthStore from '../../store/authStore';
import { Upload, Info, CircleAlert, CircleCheck, Loader2, RotateCcw, Rocket } from 'lucide-react';

export default function PgImport() {
    const { user }   = useAuthStore();
    const [tenants,  setTenants]  = useState([]);
    const [tenantId, setTenantId] = useState('');
    const [file,     setFile]     = useState(null);
    const [state,    setState]    = useState('idle'); // idle | uploading | importing | done | error
    const [pct,      setPct]      = useState(0);
    const [msg,      setMsg]      = useState('');
    const [log,      setLog]      = useState([]);
    const [summary,  setSummary]  = useState(null);
    const [error,    setError]    = useState('');
    const fileRef = useRef();
    const logRef  = useRef();

    useEffect(() => {
        api.get('/superadmin/tenants').then(r => setTenants(r.data.data || [])).catch(() => {});
    }, []);

    // Auto-scroll log
    useEffect(() => {
        if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
    }, [log]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file || !tenantId) return;

        setState('uploading');
        setError('');
        setPct(0);
        setLog([]);
        setSummary(null);

        try {
            // Gzip ilə sıxılma — 413 nginx limit-i keçmək üçün
            console.log('[PgImport] Compressing file...', file.name, file.size);
            const originalSize = file.size;
            let uploadFile = file;
            try {
                const stream = file.stream().pipeThrough(new CompressionStream('gzip'));
                const blob   = await new Response(stream).blob();
                uploadFile   = new File([blob], file.name + '.gz', { type: 'application/gzip' });
                console.log('[PgImport] Compressed', originalSize, '→', uploadFile.size, `(${Math.round(uploadFile.size / originalSize * 100)}%)`);
            } catch (compressErr) {
                console.warn('[PgImport] Compression not supported, sending raw:', compressErr.message);
            }

            // ── CHUNKED UPLOAD — nginx client_max_body_size (1MB) limit-ini keçmək üçün ──
            // Faylı 500KB-lik hissələrə bölüb ayrı-ayrı göndərir, hər hissə nginx limitindən kiçikdir
            const CHUNK_SIZE = 500 * 1024;
            const uploadId = Date.now().toString(36) + Math.random().toString(36).slice(2);
            const totalChunks = Math.ceil(uploadFile.size / CHUNK_SIZE);
            const isGz = uploadFile !== file;

            console.log(`[PgImport] Uploading ${uploadFile.size} bytes in ${totalChunks} chunks`);
            setState('importing');

            for (let i = 0; i < totalChunks; i++) {
                const start = i * CHUNK_SIZE;
                const end   = Math.min(start + CHUNK_SIZE, uploadFile.size);
                const chunk = uploadFile.slice(start, end);

                const fd = new FormData();
                fd.append('chunk', chunk, `chunk-${i}`);
                fd.append('uploadId', uploadId);
                fd.append('chunkIndex', String(i));
                fd.append('totalChunks', String(totalChunks));
                fd.append('tenantId', String(tenantId));
                fd.append('compressed', isGz ? '1' : '0');
                fd.append('originalName', file.name);

                await api.post('/pgimport/upload-chunk', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    timeout: 60000,
                });
                setMsg(`Fayl yüklənir... ${i + 1}/${totalChunks}`);
                setPct(Math.round(((i + 1) / totalChunks) * 5));
            }

            console.log('[PgImport] All chunks uploaded, finalizing...');
            setMsg('Yüklənmə tamamlandı, import başlayır...');
            const res = await api.post('/pgimport/upload-finalize', { uploadId }, { timeout: 30000 });

            console.log('[PgImport] Finalize response:', res.data);
            if (!res.data.ok) throw new Error(res.data.msg);

            const jobId = res.data.jobId;
            setState('importing');

            // POLLING (SSE əvəzinə) — proxy/CDN timeout-larından təhlükəsiz
            // Hər 2 saniyədə backend-dən status soruşur
            let missCount = 0;
            const poll = async () => {
                try {
                    const sres = await api.get(`/pgimport/status/${jobId}`);
                    const data = sres.data;
                    if (data.ok === false && data.error) {
                        // Job hələ DB-yə yazılmayıb — bir neçə cəhd ver
                        if (++missCount < 5) return;
                        clearInterval(iv);
                        setError(data.error);
                        setState('error');
                        return;
                    }
                    missCount = 0;
                    setPct(data.pct || 0);
                    setMsg(data.msg || '');
                    if (data.log?.length) setLog(data.log);
                    if (data.done) {
                        clearInterval(iv);
                        if (data.error) {
                            setError(data.error);
                            setState('error');
                        } else {
                            setSummary(data.summary);
                            setState('done');
                        }
                    }
                } catch (err) {
                    console.warn('[PgImport] Poll error:', err.message);
                    // Şəbəkə xətaları üçün dərhal vermə, cəhdə davam et
                    if (++missCount > 20) {
                        clearInterval(iv);
                        setError('Əlaqə kəsildi. Səhifəni yeniləyib təkrar cəhd edin.');
                        setState('error');
                    }
                }
            };
            const iv = setInterval(poll, 2000);
            poll(); // dərhal birinci sorğunu da göndər

        } catch (err) {
            setError(err.response?.data?.msg || err.message);
            setState('error');
        }
    };

    const reset = () => {
        setState('idle');
        setFile(null);
        setPct(0);
        setMsg('');
        setLog([]);
        setSummary(null);
        setError('');
        if (fileRef.current) fileRef.current.value = '';
    };

    const isRunning = state === 'uploading' || state === 'importing';

    return (
        <SuperadminLayout title="Baza import" subtitle="pgAdmin-dən export edilmiş plain SQL faylını tenanta köçürün">
            <div className="max-w-2xl">
                {/* Təlimat */}
                <div className="mb-5 flex gap-3 rounded-2xl border border-sky-100 bg-sky-50/70 p-4 text-sm text-sky-900">
                    <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-600" />
                    <div>
                        <p className="font-semibold">Necə export etmək olar</p>
                        <p className="mt-0.5 text-sky-800/90">pgAdmin → Sağ klik → Backup → Format: <strong>Plain</strong> → Backup</p>
                        <p className="mt-1.5 text-xs text-sky-700/80">
                            Və ya PowerShell ilə: <code className="rounded bg-white/70 px-1.5 py-0.5 font-mono">pg_restore --no-acl --no-owner -f plain.sql binary.sql</code>
                        </p>
                    </div>
                </div>

                {/* Form */}
                {state === 'idle' || state === 'error' ? (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-5 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                        <div>
                            <label className="mb-1.5 block text-xs font-medium text-slate-600">Restoran</label>
                            <select
                                value={tenantId}
                                onChange={e => setTenantId(e.target.value)}
                                required
                                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 transition focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100"
                            >
                                <option value="">Restoran seçin</option>
                                {tenants.map(t => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="mb-1.5 block text-xs font-medium text-slate-600">SQL faylı (.sql)</label>
                            <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 px-4 py-7 text-center transition-colors hover:border-[#0090bb] hover:bg-sky-50/40">
                                <Upload className="h-6 w-6 text-slate-400" />
                                <span className="mt-2 text-sm font-semibold text-slate-700">{file ? file.name : 'Fayl seçmək üçün klikləyin'}</span>
                                <span className="mt-0.5 text-xs text-slate-400">
                                    {file ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : 'Göndərməzdən əvvəl avtomatik sıxılır (gzip)'}
                                </span>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept=".sql,text/plain"
                                    required
                                    onChange={e => setFile(e.target.files?.[0] || null)}
                                    className="sr-only"
                                />
                            </label>
                        </div>

                        {error && (
                            <div className="flex gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                <CircleAlert className="mt-0.5 h-4 w-4 flex-shrink-0" /> {error}
                            </div>
                        )}

                        <div className="flex gap-3">
                            {state === 'error' && (
                                <button type="button" onClick={reset}
                                    className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-200 py-3 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50">
                                    <RotateCcw className="h-4 w-4" /> Geri
                                </button>
                            )}
                            <button
                                type="submit"
                                disabled={!file || !tenantId}
                                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#0090bb] py-3 text-sm font-semibold text-white shadow-sm shadow-sky-900/20 transition-colors hover:bg-[#007ea4] disabled:opacity-40"
                            >
                                <Rocket className="h-4 w-4" /> İmport et
                            </button>
                        </div>
                    </form>
                ) : null}

                {/* Proses */}
                {(isRunning || state === 'done') && (
                    <div className="flex flex-col gap-5 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                        <div className="flex items-center gap-3">
                            <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${state === 'done' ? 'bg-emerald-50 text-emerald-600' : 'bg-sky-50 text-[#0090bb]'}`}>
                                {state === 'done' ? <CircleCheck className="h-5 w-5" /> : <Loader2 className="h-5 w-5 animate-spin" />}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-semibold text-slate-900">{msg || (isRunning ? 'Proses gedir' : 'Tamamlandı')}</div>
                                <div className="text-xs text-slate-500">{isRunning ? 'Proses gedir, gözləyin...' : 'Tamamlandı!'}</div>
                            </div>
                            <span className="font-mono text-sm font-semibold text-slate-700">{pct}%</span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                            <div
                                className={`h-full rounded-full transition-all duration-300 ${state === 'done' ? 'bg-emerald-500' : 'bg-[#0090bb]'}`}
                                style={{ width: `${pct}%` }}
                            />
                        </div>

                        {/* Jurnal */}
                        <div
                            ref={logRef}
                            className="h-44 space-y-0.5 overflow-y-auto rounded-xl bg-[#0b1220] p-3 font-mono text-[11px] text-slate-300"
                        >
                            {log.map((line, i) => (
                                <div key={i} className={line.includes('✓') ? 'text-emerald-400' : line.includes('⚠') ? 'text-amber-300' : 'text-slate-300'}>
                                    {line}
                                </div>
                            ))}
                            {isRunning && <div className="animate-pulse text-slate-500">▌</div>}
                        </div>

                        {/* Nəticə */}
                        {summary && (
                            <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                                {[
                                    ['İnqredient', summary.ingredients],
                                    ['Menyu', summary.menuItems],
                                    ['Masa', summary.tables],
                                    ['Tədarükçü', summary.suppliers],
                                    ['Alış', summary.purchases],
                                    ['Sifariş', summary.orders],
                                    ['Xərc', summary.expenses],
                                    ['Xidmət', summary.xidmetler],
                                ].map(([label, count]) => (
                                    <div key={label} className="rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2.5">
                                        <div className="text-[11px] text-slate-500">{label}</div>
                                        <div className="text-base font-semibold text-slate-900">{count?.toLocaleString() ?? '0'}</div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {state === 'done' && (
                            <button onClick={reset}
                                className="flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50">
                                <RotateCcw className="h-4 w-4" /> Yeni import
                            </button>
                        )}
                    </div>
                )}
            </div>
        </SuperadminLayout>
    );
}
